import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { countBy } from "@lyfegen-platform/utils";
import { format } from "date-fns";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../../../models/common/data";
import {
	AgreementScope,
	BaseAgreement,
	DocumentStatus,
	DocumentType,
	FinancialDataPointStatus,
	SmartMipDocumentStatus,
	Treatment,
} from "../../../../../models/generated/graphql";
import { isScopeMatchedByScopeId } from "../../../../../utils/filter-scopes";
import {
	GetReviewInvoice_documents_agreementBuyerInvoiceInfo,
	GetReviewInvoice_documents_agreementSellerInvoiceInfo,
	GetReviewInvoice_documents_approvedBy,
	GetReviewInvoice_documents_confirmedBy,
	GetReviewInvoice_documents_rejectedBy,
} from "../types/GetReviewInvoice";
import { GetInvoiceQuery, GetReviewInvoiceQuery, RefundInvoiceQuery } from "../types/queries.generated";
import { InvoiceStatusState } from "../UpdateInvoice";
import { RefundState } from "./get-refund-info";

export type RefundWithState = GetInvoiceQuery["documents"][0]["documentRefundGroups"][0]["refunds"][0] & {
	state: RefundState;
	sentWithExternalMessageId?: string | null;
	lastExternalMessageId?: string | null;
};

export type DocumentRefundGroup = Omit<GetInvoiceQuery["documents"][0]["documentRefundGroups"][0], "refunds"> & {
	refunds: Array<RefundWithState>;
};

const getCurrencyFromDocument = (d: GetInvoiceQuery["documents"][0] | GetReviewInvoiceQuery["documents"][0] | undefined) =>
	d?.baseAgreements[0]?.currency || d?.documentRefundGroups[0].agreement?.currency || undefined;

export const mapRefundsToState = (documentRefundGroup: DocumentRefundGroup): Array<InvoiceStatusState> => {
	return documentRefundGroup.refunds!.flatMap((refund) =>
		refund.documentFinancialDataPointConnection.edges.map((edge) => ({
			fdpUuid: edge.node.generatedUuid,
			refundId: refund.id,
			amount: edge.node.refundable || 0,
			status: edge.status || FinancialDataPointStatus.Undecided,
			adjustedAmount: refund.adjustedAmount,
			adjustedRate: refund.adjustedRate,
			adjustedBy: refund.adjustedBy,
			adjustmentComment: refund.adjustmentComment,
			lastAdjustedAt: refund.lastAdjustedAt,
		}))
	);
};

export const mapValues = (data: GetInvoiceQuery | GetReviewInvoiceQuery | undefined) => {
	if (!data || !data.documents || !data.documents.length) {
		return {
			coveragePeriod: EMPTY_VALUE_PLACEHOLDER,
			periodDateType: undefined,
			coveragePeriodStart: undefined,
			coveragePeriodEnd: undefined,
			agreementTags: undefined,
			products: null,
			createdOn: "",
			invoiceCurrency: "CHF",
			invoiceStatus: null,
			tenantIds: [],
			currency: undefined,
			documentRefundGroups: [] as Array<DocumentRefundGroup>,
			agreementIds: [],
			documentType: DocumentType.Invoice,
			invoiceAmount: 0,
			areFiltersVisible: true,
			agreementsWithoutSufficientAccess: [],
			counterparty: undefined,
			hasCaselessTransactions: false,
			hasPatientTransactions: false,
			agreementBuyerInvoiceInfo: undefined,
			agreementSellerInvoiceInfo: undefined,
			isAgreementBuyer: undefined,
			initialInvoiceState: [],
			baseAgreements: [],
			messages: [],
			uploadedVariantKey: null,
			uploadedOn: null,
			dunningDate: null,
		};
	}

	const d = data.documents[0];
	const status = d?.status;
	const areFiltersVisible = [DocumentStatus.WorkingDraft, DocumentStatus.InReview, DocumentStatus.InApproval].includes(
		status as DocumentStatus
	);
	const agreementIds = new Set<string>();
	const allProducts = new Set<string>();
	const baseAgreements = d.baseAgreements as Array<BaseAgreement>;
	const documentRefundGroups = d.documentRefundGroups.map((documentRefundGroup) => ({
		...documentRefundGroup,
		refunds: documentRefundGroup.refunds
			.filter(
				(refund) =>
					!refund.isDeleted || refund.documentFinancialDataPointConnection.edges[0]?.status === FinancialDataPointStatus.Removed
			)
			.filter(
				(refund) =>
					areFiltersVisible ||
					(refund.documentFinancialDataPointConnection.edges[0]?.status &&
						[FinancialDataPointStatus.Approved, FinancialDataPointStatus.Declined, FinancialDataPointStatus.Removed].includes(
							refund.documentFinancialDataPointConnection.edges[0].status
						))
			)
			.map((refund) => {
				allProducts.add(refund.product);

				if (documentRefundGroup.case) {
					agreementIds.add(documentRefundGroup.case.actualAgreementId!);
				}

				if (documentRefundGroup.agreement) {
					agreementIds.add(documentRefundGroup.agreement.id);
				}

				return {
					...refund,
					state:
						refund.documentFinancialDataPointConnection.edges[0]?.status === FinancialDataPointStatus.Removed
							? RefundState.Deleted
							: RefundState.Active,
				};
			}),
	})) as Array<DocumentRefundGroup>;

	const coveragePeriod = d.coveragePeriod ? d.coveragePeriod : "";
	const periodDateType = d?.periodDateType;
	const agreementTags = Array.from(
		new Set(
			[
				...d.documentRefundGroups.flatMap((documentRefundGroup) => documentRefundGroup.case?.agreementTag),
				...d.documentRefundGroups.flatMap((documentRefundGroup) => documentRefundGroup.agreement?.tags.flatMap((tag) => tag.type)),
			].filter((tag) => !!tag) || []
		)
	);

	const products = Array.from(allProducts).join(", ") || "-";
	const createdOn = format(new Date(d?.createdOn || ""), "yyyy-MM-dd") || "-";
	const currency = getCurrencyFromDocument(d);
	const defaultAssigneeIssuer = d?.createdByEmail;
	const invoiceStatus = d?.status;
	const invoiceAmount = d?.invoiceAmount || 0;
	const tenantIds = d?.tenantIds || [];
	const creatorTenantId = d?.creatorTenantId;
	const documentType = d?.type || DocumentType.Invoice;
	const isDeleted: boolean = d?.isDeleted || false;
	const hasCaselessTransactions: boolean = d?.hasCaselessTransactions || false;
	const hasPatientTransactions: boolean = d?.hasPatientTransactions || false;
	const canUserDelete: boolean = d?.canUserDelete || false;
	const agreementsWithoutUserAccess: Array<string> = data.documents.flatMap((document) =>
		document.agreementsWithoutUserAccess.flatMap((role) => role.id)
	);
	const agreementsWithOnlyViewingFullAccess: Array<string> = data.documents.flatMap((document) =>
		document.agreementsWithOnlyViewingFullAccess.flatMap((agreement) => agreement.name)
	);

	const agreementsWithoutSufficientAccess: Array<string> = [
		...data.documents.flatMap((document) => document.agreementsWithoutUserAccess.flatMap((role) => role.name)),
		...agreementsWithOnlyViewingFullAccess,
	];

	const counterparty = d.isAgreementBuyer ? d.agreementSellerInvoiceInfo?.name : d.agreementBuyerInvoiceInfo?.name;
	const agreementBuyerInvoiceInfo = d.agreementBuyerInvoiceInfo;
	const agreementSellerInvoiceInfo = d.agreementSellerInvoiceInfo;
	const isAgreementBuyer = d?.isAgreementBuyer || null;

	const initialInvoiceState = d.documentRefundGroups.flatMap((drg) => mapRefundsToState(drg as DocumentRefundGroup));
	const messages = d.messages;
	const uploadedVariantKey = d.uploadedVariantKey;
	const uploadedOn = d.uploadedOn;
	const dunningDate = d.dunningDate;
	const beneficiary = (d as { beneficiary?: string | null }).beneficiary;
	const paymentReferenceLyfegen = (d as { paymentReferenceLyfegen?: string | null }).paymentReferenceLyfegen;
	const paymentReceiptPayer = (d as { paymentReceiptPayer?: string | null }).paymentReceiptPayer;

	return {
		coveragePeriod,
		periodDateType,
		agreementTags,
		products,
		createdOn,
		status,
		messages,
		initialInvoiceState,
		documentRefundGroups,
		defaultAssigneeIssuer,
		invoiceStatus,
		baseAgreements,
		tenantIds,
		isDeleted,
		canUserDelete,
		creatorTenantId,
		currency,
		agreementsWithoutUserAccess,
		documentType,
		invoiceAmount,
		agreementIds: Array.from(agreementIds),
		areFiltersVisible,
		agreementsWithOnlyViewingFullAccess,
		agreementsWithoutSufficientAccess,
		counterparty,
		hasCaselessTransactions,
		hasPatientTransactions,
		isAgreementBuyer,
		agreementBuyerInvoiceInfo,
		agreementSellerInvoiceInfo,
		uploadedVariantKey,
		uploadedOn,
		dunningDate,
		beneficiary,
		paymentReferenceLyfegen,
		paymentReceiptPayer,
	};
};

export const mapReviewValues = (data: GetReviewInvoiceQuery | undefined) => {
	if (!data || !data.documents || !data.documents.length) {
		return {
			...mapValues(data as GetReviewInvoiceQuery),
			assignedTo: undefined,
			isAgreementBuyer: undefined,
			isAgreementSeller: undefined,
			isAgreementOwner: undefined,
			canAssignUser: undefined,
			canConfirm: false,
			canApprove: false,
			isAssignee: undefined,
			initialInvoiceState: [],
			issuedOn: null,
			lastChangedOn: null,
			lastRemindedOn: null,
			paymentReminderEmail: null,
			isConfirmedBySeller: false,
			invoiceSettledOn: EMPTY_VALUE_PLACEHOLDER,
			documentType: DocumentType.Invoice,
			agreementBuyerInvoiceInfo: undefined,
			agreementSellerInvoiceInfo: undefined,
			isSentForExternalReview: false,
			isConfirmedByExternalParty: false,
			isConfirmedBySender: false,
			isConfirmedByBuyer: false,
			isSentToSmartMip: false,
			smartMipStatus: null,
			currentReviewerOrganization: undefined,
			confirmedBy: [] as Array<GetReviewInvoice_documents_confirmedBy>,
			approvedBy: [] as Array<GetReviewInvoice_documents_approvedBy>,
			rejectedBy: [] as Array<GetReviewInvoice_documents_rejectedBy>,
			baseAgreements: [] as Array<BaseAgreement>,
			sentToReviewOn: null,
			reviewRemindedOn: null,
			uploadedVariantKey: null,
			uploadedOn: null,
			dunningDate: null,
			isCommentRequierd: false,
			canManageRefunds: false,
			smartMipReimbursementId: null,
		};
	}

	const d = data?.documents && data?.documents[0];
	const isAgreementBuyer = d?.isAgreementBuyer || null;
	const isAgreementSeller = d?.isAgreementSeller || null;
	const isAgreementOwner = d?.isAgreementOwner || null;
	const canAssignUser = d?.canAssignUser || null;
	const isAssignee = d?.isAssignee || null;
	const isCommentRequierd = d?.isCommentRequierd || false;
	const initialInvoiceState = d.documentRefundGroups.flatMap((drg) => mapRefundsToState(drg as DocumentRefundGroup));
	const issuedOn = d?.issuedOn;
	const isConfirmedBySeller = d?.isConfirmedBySeller;
	const isConfirmedBySender = d?.isConfirmedBySender;
	const isConfirmedByBuyer = d?.isConfirmedByBuyer;
	const invoiceSettledOn = d?.settledOn;
	const documentType = d?.type || DocumentType.Invoice;
	const currency = getCurrencyFromDocument(d);
	const lastChangedOn = d?.lastChangedOn;
	const lastRemindedOn = d?.lastRemindedOn;
	const reviewRemindedOn = d?.reviewRemindedOn;
	const paymentReminderEmail = d?.paymentReminderEmail;
	const sentToReviewOn = d?.sentToReviewOn;
	const currentReviewerOrganization = d.currentReviewerOrganization;
	const mappedValues = mapValues(data);

	return {
		...mappedValues,
		isAgreementBuyer,
		isAgreementSeller,
		isAgreementOwner,
		isAssignee,
		canAssignUser,
		initialInvoiceState,
		issuedOn,
		isConfirmedBySeller,
		isConfirmedBySender,
		isConfirmedByBuyer,
		invoiceSettledOn,
		currency,
		documentType,
		lastChangedOn,
		lastRemindedOn,
		paymentReminderEmail,
		agreementBuyerInvoiceInfo: d.agreementBuyerInvoiceInfo,
		agreementSellerInvoiceInfo: d.agreementSellerInvoiceInfo,
		confirmedBy: d.confirmedBy,
		approvedBy: d.approvedBy,
		rejectedBy: d.rejectedBy,
		canConfirm: !!d.canConfirm,
		canApprove: !!d.canApprove,
		isSentForExternalReview: !!d.isSentForExternalReview,
		isConfirmedByExternalParty:
			!!d.isConfirmedByExternalParty || (!!d.isSentToSmartMip && d.smartMipStatus === SmartMipDocumentStatus.FullySigned),
		isSentToSmartMip: !!d.isSentToSmartMip,
		smartMipStatus: d.smartMipStatus ?? null,
		smartMipReimbursementId: d.smartMipReimbursementId,
		currentReviewerOrganization,
		reviewRemindedOn,
		sentToReviewOn,
		isCommentRequierd,
		canManageRefunds:
			(mappedValues.status === DocumentStatus.WorkingDraft || mappedValues.status === DocumentStatus.InReview) &&
			(!d.isSentToSmartMip ||
				(d.smartMipStatus === SmartMipDocumentStatus.FurtherEnquiry &&
					currentReviewerOrganization ===
						(documentType === DocumentType.Invoice ? d.agreementBuyerInvoiceInfo?.name : d.agreementSellerInvoiceInfo?.name))),
	};
};

export const getGroupScopes = (group: DocumentRefundGroup, baseAgreements: Array<BaseAgreement>) => {
	const matchedScopeId = JSON.parse(group.case?.flexEntitiesConnection?.edges[0]?.node.data || "{}")[AttributeIdentifiers.ScopeId];
	const baseAgreement = baseAgreements.find((a) => a.id === group.case?.actualAgreementId);
	const agreementScopeFromMatchedScopeId = [baseAgreement, ...(baseAgreement?.nextVersions ?? [])]
		.filter(Boolean)
		.flatMap((a) => a!.enrollingConditionSets.flatMap((conditionSet) => conditionSet.scopes))
		.find((scope) => isScopeMatchedByScopeId(scope as unknown as AgreementScope, matchedScopeId));

	const baseScope = agreementScopeFromMatchedScopeId || baseAgreement?.enrollingConditionSets[0].scopes[0];
	const countryScopeId = baseScope?.countryScopesConnection?.edges?.[0]?.countryScopeId ?? undefined;
	const organizationScopeId = baseScope?.organizationScopesConnection?.edges[0]?.organizationScopeId ?? undefined;

	return { countryScopeId, organizationScopeId };
};

export const getRefundGroupInfo = (group: DocumentRefundGroup | undefined, baseAgreements: Array<BaseAgreement>) => {
	if (!group) {
		return {
			isCaseless: false,
			agreement: null,
			caseEntity: null,
			currency: null,
			packs: [],
		};
	}

	const caseEntity = group.case;
	const isCaseless = !caseEntity && group.agreement?.isCaseless;
	const baseAgreement = baseAgreements.find((a) => a.id === group.case?.actualAgreementId);
	const agreement = isCaseless ? group.agreement : baseAgreement!;
	const currencyCode = agreement?.currency?.Code;
	const packs = group.refunds.flatMap((r) =>
		r.documentFinancialDataPointConnection.edges.map((e) => e.node.treatment)
	) as Array<Treatment>;
	const purchaseId = group.refunds[0]?.documentFinancialDataPointConnection.edges[0].node.evaluates.purchaseId ?? undefined;

	return { isCaseless, caseEntity, agreement, currencyCode, packs, purchaseId };
};

export const getProgressInfo = ({ refunds }: DocumentRefundGroup) => {
	const selector = (refund: RefundWithState) =>
		refund.documentFinancialDataPointConnection.edges[0].status || FinancialDataPointStatus.Undecided;
	const removedCount = countBy(refunds, selector, FinancialDataPointStatus.Removed);

	return {
		declinedCount: countBy(refunds, selector, FinancialDataPointStatus.Declined),
		acceptedCount: countBy(refunds, selector, FinancialDataPointStatus.Approved),
		reviewedCount: countBy(refunds, selector, FinancialDataPointStatus.Undecided, "different") - removedCount,
		total: refunds.length - removedCount,
	};
};

export const getCombinedScopeIds = ({
	organizationScopeId,
	countryScopeId,
	includePlaceholder = false,
}: {
	organizationScopeId?: string;
	countryScopeId?: string;
	includePlaceholder?: boolean;
}) => {
	const result = [organizationScopeId, countryScopeId].filter(Boolean).join(", ");
	if (includePlaceholder) {
		return result || EMPTY_VALUE_PLACEHOLDER;
	}

	return result || undefined;
};

export const refundNodeSelector = (refund: RefundWithState) => refund.documentFinancialDataPointConnection.edges[0].node;

export interface GetDualApprovalArgs {
	isSeller: boolean;
	isBuyer: boolean;
	invoiceAmount: number | null | undefined;
	agreementBuyerInvoiceInfo:
		| GetReviewInvoice_documents_agreementBuyerInvoiceInfo
		| null
		| undefined
		| RefundInvoiceQuery["documents"][number]["agreementBuyerInvoiceInfo"];
	agreementSellerInvoiceInfo:
		| GetReviewInvoice_documents_agreementSellerInvoiceInfo
		| null
		| undefined
		| RefundInvoiceQuery["documents"][number]["agreementSellerInvoiceInfo"];
	invoiceStatus: DocumentStatus | null | undefined;
	isExposure?: boolean;
	isDualApprovalSupported: boolean;
	disregardingStatus?: boolean;
}

export const getIsDualApprovalEnabled = (args: GetDualApprovalArgs): boolean => {
	const {
		invoiceStatus,
		invoiceAmount,
		agreementBuyerInvoiceInfo,
		agreementSellerInvoiceInfo,
		isBuyer,
		isSeller,
		isDualApprovalSupported,
		isExposure,
		disregardingStatus,
	} = args;

	if (
		(!disregardingStatus && invoiceStatus !== DocumentStatus.InReview && invoiceStatus !== DocumentStatus.InApproval) ||
		!isDualApprovalSupported ||
		isExposure
	) {
		return false;
	}

	const amount = invoiceAmount || 0;

	return (
		(isBuyer && !!agreementBuyerInvoiceInfo?.dualApprovalThreshold && agreementBuyerInvoiceInfo?.dualApprovalThreshold <= amount) ||
		(isSeller && !!agreementSellerInvoiceInfo?.dualApprovalThreshold && agreementSellerInvoiceInfo?.dualApprovalThreshold <= amount)
	);
};
